import React from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import {
  ArrowForward,
  ArrowOutward,
  AccountBalanceWalletOutlined as DebtIcon,
  Business as BusinessIcon,
  CheckCircleOutline,
  Help as HelpIcon,
  Inventory2Outlined as InventoryIcon,
  PeopleAltOutlined as PeopleIcon,
  PointOfSaleOutlined as SalesIcon,
  TrendingUpOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import WhatsAppButton from '../components/common/WhatsAppButton';
import PublicHeader from '../components/public/PublicHeader';
import PageIconTile from '../components/common/PageIconTile';
import BrandLockup from '../components/brand/BrandLockup';
import PublicFooter from '../components/public/PublicFooter';
import { PageAccentName } from '../theme/pageAccents';
import './Landing.css';

const Landing: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useLanguage();

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const features: Array<{ icon: React.ReactNode; accent: PageAccentName; title: string; description: string }> = [
    {
      icon: <PeopleIcon />,
      accent: 'customers',
      title: t('landing.featureCustomersTitle'),
      description: t('landing.featureCustomersDesc'),
    },
    {
      icon: <InventoryIcon />,
      accent: 'items',
      title: t('landing.featureInventoryTitle'),
      description: t('landing.featureInventoryDesc'),
    },
    {
      icon: <SalesIcon />,
      accent: 'sales',
      title: t('landing.featureSalesTitle'),
      description: t('landing.featureSalesDesc'),
    },
    {
      icon: <DebtIcon />,
      accent: 'debts',
      title: t('landing.featureDebtsTitle'),
      description: t('landing.featureDebtsDesc'),
    },
    {
      icon: <BusinessIcon />,
      accent: 'companies',
      title: t('landing.featureMultiTenantTitle'),
      description: t('landing.featureMultiTenantDesc'),
    },
  ];

  const previewStats = [
    { icon: <PeopleIcon />, accent: 'customers' as const, label: features[0].title, value: '39' },
    { icon: <InventoryIcon />, accent: 'items' as const, label: features[1].title, value: '126' },
    { icon: <SalesIcon />, accent: 'sales' as const, label: t('landing.metricSales'), value: '₺8.420' },
    { icon: <DebtIcon />, accent: 'debts' as const, label: t('landing.metricBalance'), value: '₺12.480' },
  ];

  return (
    <Box className="landing-shell" sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicHeader />

      <Box component="main">
        <Box component="section" className="landing-hero" aria-labelledby="landing-title">
          <span className="landing-orbit landing-orbit-one" aria-hidden="true" />
          <span className="landing-orbit landing-orbit-two" aria-hidden="true" />
          <span className="landing-hero-glow" aria-hidden="true" />
          <Container maxWidth="lg" className="landing-hero-inner">
            <Box className="landing-hero-copy landing-enter">
              <Box className="landing-eyebrow">
                <span className="landing-eyebrow-dot" aria-hidden="true" />
                <Typography component="span" variant="overline">{t('landing.eyebrow')}</Typography>
              </Box>
              <Typography
                id="landing-title"
                component="h1"
                className="landing-title"
                sx={{ fontSize: { xs: '2.65rem', sm: '3.6rem', md: '4.35rem' } }}
              >
                {t('landing.heroTitle')}
              </Typography>
              <Typography className="landing-hero-subtitle">
                {t('landing.heroSubtitle')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} className="landing-hero-actions">
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  className="landing-button-gold"
                >
                  {t('landing.getStarted')}
                </Button>
                <Button
                  component={RouterLink}
                  to="/help"
                  variant="outlined"
                  size="large"
                  startIcon={<HelpIcon />}
                  className="landing-button-outline"
                >
                  {t('landing.learnMore')}
                </Button>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} className="landing-reassurance">
                <CheckCircleOutline fontSize="small" />
                <Typography variant="body2">{t('landing.footerNote')}</Typography>
              </Stack>
            </Box>

            <Paper className="landing-dashboard landing-enter landing-enter-late" elevation={0}>
              <Box className="landing-dashboard-topbar">
                <BrandLockup markSize={30} fontSize="1.18rem" gap={0.55} onDarkSurface={false} />
              </Box>
              <Divider />
              <Box className="landing-dashboard-content">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2.3 }}>
                  <Box>
                    <Typography className="landing-preview-eyebrow">{t('landing.previewLabel')}</Typography>
                    <Typography className="landing-preview-title" variant="h6" fontWeight={800} letterSpacing="-.025em">{t('landing.workflowTitle')}</Typography>
                  </Box>
                  <TrendingUpOutlined className="landing-preview-trend" />
                </Stack>
                <Box className="landing-stat-grid">
                  {previewStats.map((stat) => (
                    <Paper className="landing-stat-card" variant="outlined" key={stat.label}>
                      <PageIconTile icon={stat.icon} accent={stat.accent} size={34} iconSize={19} />
                      <Typography className="landing-stat-label">{stat.label}</Typography>
                      <Typography className="landing-stat-value">{stat.value}</Typography>
                    </Paper>
                  ))}
                </Box>
                <Paper className="landing-activity-card" variant="outlined">
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography className="landing-activity-label" variant="body2">{t('landing.activityTitle')}</Typography>
                      <Typography className="landing-activity-heading" variant="subtitle1" fontWeight={800}>{t('landing.metricSales')}</Typography>
                    </Box>
                    <Box className="landing-activity-total">+18%</Box>
                  </Stack>
                  <Box className="landing-chart" role="img" aria-label={t('landing.activityTitle')}>
                    {[34, 53, 42, 72, 56, 88, 68, 100, 76, 91, 62, 82].map((height, index) => (
                      <span key={`${height}-${index}`} style={{ height: `${height}%` }} />
                    ))}
                  </Box>
                  <Box className="landing-chart-baseline" />
                </Paper>
              </Box>
              <Box className="landing-dashboard-corner" aria-hidden="true" />
            </Paper>
          </Container>
        </Box>

        <Container maxWidth="lg" className="landing-content">
          <Box component="section" aria-labelledby="landing-features-title" className="landing-features-section">
            <Box className="landing-section-heading landing-enter">
              <Typography className="landing-section-kicker">{t('landing.eyebrow')}</Typography>
              <Typography id="landing-features-title" component="h2" variant="h3" fontWeight={800} letterSpacing="-.04em">
                {t('landing.featuresTitle')}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1.25, maxWidth: 610 }}>
                {t('landing.featuresSubtitle')}
              </Typography>
            </Box>

            <Box className="landing-feature-grid">
              {features.map((feature) => (
                <Card className="landing-feature-card" variant="outlined" key={feature.title}>
                  <CardContent className="landing-feature-card-content">
                    <Box className="landing-feature-icon">
                      <PageIconTile icon={feature.icon} accent={feature.accent} size={48} iconSize={26} />
                      <ArrowOutward className="landing-feature-arrow" />
                    </Box>
                    <Typography variant="h6" component="h3" fontWeight={800} sx={{ mt: 2.5, mb: 0.8 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          <Box component="section" className="landing-cta-section" aria-labelledby="landing-cta-title">
            <Box className="landing-cta-decoration" aria-hidden="true" />
            <Box className="landing-cta-copy">
              <Typography className="landing-section-kicker">{t('landing.eyebrow')}</Typography>
              <Typography id="landing-cta-title" component="h2" variant="h3" fontWeight={800} letterSpacing="-.04em">
                {t('landing.ctaTitle')}
              </Typography>
              <Typography sx={{ mt: 1.25, maxWidth: 650, color: 'rgba(255,255,255,.76)' }}>
                {t('landing.ctaSubtitle')}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} className="landing-cta-actions">
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                className="landing-button-gold landing-button-account"
              >
                {t('landing.createAccount')}
              </Button>
              <Button component={RouterLink} to="/login" variant="outlined" size="large" className="landing-button-outline">
                {t('landing.login')}
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      <PublicFooter />

      <WhatsAppButton />
    </Box>
  );
};

export default Landing;
